/**
 * 3대 AI 기능(리포트/계약서/제안서) 공통 이용권 게이트
 * - 이용권이 있으면 children(주로 "생성하기" 버튼) 노출
 * - 없으면:
 *   - iOS 앱: 결제 수단에 대한 언급·버튼·외부 이동을 전혀 노출하지 않고 "이용권 부족" 상태만 표시
 *     (Apple 심사 가이드라인 3.1.1 — 텍스트 언급만으로도 리젝 사유가 될 수 있어 완전히 배제, 2026-08 결정)
 *   - Android 앱: "결제는 PC나 웹 브라우저에서 진행해 주세요" 안내 + 외부 브라우저로 위임 (Google Play는 허용)
 *   - 웹: /ai-credits/:toolType 로 이동
 * project_proposal.md 4.0 결제 채널 정책 참조 — 앱 내 가격/결제 버튼 노출 금지
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { isNativeApp, isIOSApp, openAICreditsExternally } from '../utils/appBridge';
import { AIToolType } from '../api/aiCreditsAPI';

interface Props {
  toolType: AIToolType;
  toolLabel: string;
  creditsRemaining: number;
  children: React.ReactNode;
  accent?: string;
}

const AICreditGate: React.FC<Props> = ({ toolType, toolLabel, creditsRemaining, children, accent = '#a78bfa' }) => {
  const navigate = useNavigate();

  if (creditsRemaining > 0) {
    return (
      <div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 10 }}>
          {toolLabel} 이용권 {creditsRemaining}건 남음
        </div>
        {children}
      </div>
    );
  }

  // iOS 앱: 결제 수단/경로에 대한 어떤 언급·버튼·링크도 노출하지 않음 — 잠김 상태만 표시.
  if (isIOSApp()) {
    return (
      <div style={{
        textAlign: 'center', padding: '28px 20px', borderRadius: 12,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <Lock size={26} color="rgba(255,255,255,0.3)" style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
          {toolLabel} 이용권이 부족합니다.
        </div>
      </div>
    );
  }

  const handleGoPurchase = async () => {
    if (isNativeApp()) {
      await openAICreditsExternally(toolType);
    } else {
      navigate(`/ai-credits/${toolType}`);
    }
  };

  return (
    <div style={{
      textAlign: 'center', padding: '28px 20px', borderRadius: 12,
      background: 'rgba(167,139,250,0.06)', border: `1px solid ${accent}33`,
    }}>
      <Lock size={26} color={accent} style={{ marginBottom: 10 }} />
      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 16, lineHeight: 1.6 }}>
        {toolLabel} 이용권이 없습니다.<br />건당 490원 / 12건 묶음 4,990원
      </div>
      <button
        onClick={handleGoPurchase}
        style={{
          padding: '12px 24px', borderRadius: 10, border: 'none',
          background: accent, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}
      >
        {isNativeApp() ? '결제는 PC나 웹 브라우저에서 진행해 주세요' : '이용권 구매하기'}
      </button>
    </div>
  );
};

export default AICreditGate;
